import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AdminUser, AdminLoginCredentials, AdminAuthState } from '../types';
import { adminAuthApi } from '../services/api';

// Admin Auth Actions
type AdminAuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: AdminUser; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

// Initial state
const initialState: AdminAuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isAdmin: false,
};

// Admin Auth reducer
const adminAuthReducer = (state: AdminAuthState, action: AdminAuthAction): AdminAuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isAdmin: action.payload.user.role === 'admin',
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
};

// Admin Auth context
interface AdminAuthContextType extends AdminAuthState {
  login: (credentials: AdminLoginCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

// Admin Auth provider
interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(adminAuthReducer, initialState);

  // Check for existing admin token on mount
  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = localStorage.getItem('admin_auth_token');
      if (token) {
        try {
          dispatch({ type: 'SET_LOADING', payload: true });
          const response = await adminAuthApi.verifyToken();
          if (response.status === 'success' && response.data && response.data.user && response.data.user.role === 'admin') {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user: response.data.user, token },
            });
          } else {
            throw new Error('Invalid admin access');
          }
        } catch (error) {
          localStorage.removeItem('admin_auth_token');
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAdminAuth();
  }, []);

  const login = async (credentials: AdminLoginCredentials) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await adminAuthApi.login(credentials);
      if (response.status === 'success' && response.data) {
        localStorage.setItem('admin_auth_token', response.data.token);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: response.data.user, token: response.data.token },
        });
      } else {
        throw new Error(response.message || 'Invalid response from server');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Admin login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_auth_token');
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AdminAuthContextType = {
    ...state,
    login,
    logout,
    clearError,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

// Admin Auth hook
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export default AdminAuthContext;
